import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { StorageDocumentLink } from '../../../components/storage-document-link/index';
import { SummaryItem } from '../../../components/summary-list/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useSelected } from '../../../hooks/company/index';
import { SafeHooks, useFormat } from '../../../hooks/index';
import { InvestmentRoundingSettingTitle } from '../../../types/investment-rounding.types';
import {
  ConversionMethod,
  getConversionMethodTitle,
  Safe,
  safeCapTypeTitle,
} from '../../../types/safes.types';
import { downloadCompanyStorageFile } from '../../../utils/company-storage-url.util';
import { toRound } from '../../../utils/get-rounded-number.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { toMathRound } from '../../../utils/to-math-round.util';
import { toNumber } from '../../../utils/to-number.util';
import { RecordAttachedFilesPreview } from '../../pool-plans/components/record-attached-files-preview';
import { SafeDropDown } from './conversion-summary/index';

type ModalProps = {
  companyId: string;
  conversionId: string;
  handleClose: () => void;
  isOpenModal: boolean;
};

export const ViewConversionModal: FC<ModalProps> = ({
  companyId,
  isOpenModal,
  handleClose,
  conversionId,
}) => {
  const { format } = useFormat();
  const { selectedCompany } = useSelected();
  const { safeConversion } = SafeHooks.useSafeConversion({
    companyId,
    conversionId,
    enabled: isOpenModal,
  });
  const valuation = safeConversion?.valuation;

  const preMoneyValuation =
    toNumber(valuation?.issuedSharesOnStart) * toNumber(valuation?.sharePrice);
  const investment = safeConversion?.items?.reduce((acc, item) => {
    acc += item.investment;
    return acc;
  }, 0);
  const convertedShares = safeConversion?.convertedShares || 0;
  // Each item's `capitalOwned` is its share of the post-conversion cap table under the method
  // that actually applied (pre-money cap, post-money cap, discount or floor), so the sum is the
  // group's ownership — `investment / cap` would only be right for post-money caps.
  const totalOwnership = safeConversion?.items?.reduce((acc, item) => {
    acc += toNumber(item.capitalOwned);
    return acc;
  }, 0);
  const handleDocumentClick = (fileLink: string) => {
    void downloadCompanyStorageFile(companyId, fileLink);
  };

  return (
    <Sheet onOpenChange={(open) => !open && handleClose()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleClose}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="VIEW_SAFE_DETAILS" />
            </span>
            <CloseModalButton onClose={handleClose} />
          </div>
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                <span className="px-4 text-sm font-[550] text-gray-700">
                  <AppFormattedMessage id="GENERAL" />
                </span>
                <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
                  <SummaryItem
                    className="pt-0"
                    title={<AppFormattedMessage id="CONVERSION_DATE" />}
                    value={format(safeConversion?.conversionDate, 'dd/MM/yyyy')}
                  />

                  <SummaryItem
                    title={<AppFormattedMessage id="LINKED_EVENT" />}
                    value={valuation?.name}
                  />
                  <SummaryItem
                    title={<AppFormattedMessage id="SHARE_CLASS" />}
                    value={safeConversion?.shareClass?.name}
                  />
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="ROUNDING_SETTINGS" />}
                    value={
                      safeConversion?.roundingSetting ? (
                        <AppFormattedMessage
                          id={InvestmentRoundingSettingTitle[safeConversion.roundingSetting]}
                        />
                      ) : (
                        <AppFormattedMessage id="ROUNDING_SETTINGS_DEFAULT" />
                      )
                    }
                  />
                </div>
              </div>

              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                <span className="px-4 text-sm font-[550] text-gray-700">
                  <AppFormattedMessage id="SELECTED_SAFES" />
                </span>
                <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4 py-1">
                  {safeConversion?.items?.map((safe) => (
                    <SummaryItem
                      key={safe.id}
                      title={
                        <Tooltip content={safe?.stakeholder?.fullName}>
                          {safe?.stakeholder?.fullName}
                        </Tooltip>
                      }
                      titleClassName="truncate w-[220px]"
                      value={`${selectedCompany?.currency?.symbol} ${safe?.investment.toLocaleString('en-US')} ${format(safe?.issueDate, 'dd/MM/yyyy')}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                <span className="px-4 text-sm font-[550] text-gray-700">
                  <AppFormattedMessage id="GENERAL_CONVERSION_SUMMARY" />
                </span>
                <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4 py-1">
                  <SummaryItem
                    title={<AppFormattedMessage id="EVENT_SHARE_PRICE_POST_CONVERSION" />}
                    value={`${selectedCompany?.currency?.symbol} ${toMathRound(safeConversion?.postConversionSharePrice)}`}
                  />
                  <SummaryItem
                    title={<AppFormattedMessage id="SAFE_INVESTMENT_CONVERSION_SUM" />}
                    value={`${selectedCompany?.currency?.symbol} ${investment?.toLocaleString('en-US')}`}
                  />
                  <SummaryItem
                    title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
                    value={`${selectedCompany?.currency?.symbol}  ${preMoneyValuation?.toLocaleString('en-US')}`}
                  />
                  <SummaryItem
                    title={<AppFormattedMessage id="TOTAL_PERCENT_OWNED_SAFE" />}
                    value={`${parseFloat(((totalOwnership || 0) * 100).toFixed(2))}%`}
                  />
                  <SummaryItem
                    title={<AppFormattedMessage id="CONVERTED_SHARES" />}
                    value={toRound(convertedShares, 0).toLocaleString('en-US')}
                  />
                </div>
              </div>

              <div className="mt-2 flex w-full flex-col gap-4 rounded-lg p-4 shadow-sm">
                <span className="text-label-md font-[500] text-gray-500">
                  <AppFormattedMessage id="CONVERSION_DETAILS" />
                </span>
                <div className="flex w-full flex-col gap-2">
                  {safeConversion?.items?.map((safeItem) => (
                    <>
                      <ViewConversionModalItem
                        currencySign={selectedCompany?.currency?.symbol}
                        item={safeItem}
                        key={safeItem.id}
                        onDocumentClick={handleDocumentClick}
                      />
                    </>
                  ))}
                </div>
              </div>

              <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white px-4 py-2">
                <SummaryItem
                  className="pb-0 pt-0"
                  title={<AppFormattedMessage id="ATTACHED_FILES_TITLE" />}
                  titleClassName="w-fit"
                  titleWrapperClassName="w-[120px]"
                  value={
                    <div className="flex max-w-[220px] flex-col gap-2">
                      {safeConversion?.filesLinks?.length ? (
                        safeConversion?.filesLinks.map((link) => (
                          <StorageDocumentLink
                            key={link}
                            label={getS3FileOriginalName(link) || link}
                            link={link}
                            onDocumentClick={handleDocumentClick}
                          />
                        ))
                      ) : (
                        <span className="truncate text-xs font-[450] text-gray-700">-</span>
                      )}
                    </div>
                  }
                />
              </div>
              <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
                <div className="flex w-full gap-3 px-4 py-2">
                  <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
                    {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
                  </span>
                  <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
                    {safeConversion?.additionalNotes || '-'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex h-9 w-full justify-end gap-3 px-6">
              <Button
                className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
                onClick={handleClose}
                type="button"
              >
                <AppFormattedMessage id="CLOSE" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ViewConversionModalItem: FC<{
  currencySign?: string;
  item: Safe;
  onDocumentClick: (fileLink: string) => void;
}> = ({ item, currencySign, onDocumentClick }) => {
  const {
    stakeholder,
    investment,
    valuationCapOwnership,
    discountOwnership,
    floorOwnership,
    conversionMethod,
    convertedShares,
    filesLinks,
    docusignFileLinks,
    capitalOwned,
    capType,
    capValue,
  } = item;
  const conversionSharePrice = item.isConvertedToShares ? item.conversionSharePrice : null;

  return (
    <SafeDropDown title={stakeholder?.fullName}>
      <div className="flex w-full flex-col divide-y-[1px] divide-gray-100">
        <SummaryItem
          className="pt-0"
          title={<AppFormattedMessage id="SAFE_INVESTMENT_CONVERSION" />}
          value={`${currencySign} ${investment.toLocaleString('en-US')}`}
        />
        <SummaryItem
          title={<AppFormattedMessage id="CAP_TYPE" />}
          value={capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-'}
        />
        <SummaryItem
          title={<AppFormattedMessage id="VALUATION_CAP_PERCENT" />}
          value={`${toRound(valuationCapOwnership * 100)}%`}
        />
        <SummaryItem
          title={<AppFormattedMessage id="DISCOUNT_PERCENT" />}
          value={`${toRound(discountOwnership * 100)}%`}
        />
        <SummaryItem
          title={<AppFormattedMessage id="FLOOR_PERCENT" />}
          value={`${toRound(floorOwnership * 100)}%`}
        />
        <SummaryItem
          title={<AppFormattedMessage id="METHOD_OF_CONVERSION" />}
          value={
            <AppFormattedMessage
              id={getConversionMethodTitle(conversionMethod || ConversionMethod.DISCOUNT, capType)}
            />
          }
        />
        <SummaryItem
          title={<AppFormattedMessage id="CONVERSION_SHARE_PRICE" />}
          value={
            conversionSharePrice
              ? `${currencySign} ${toMathRound(conversionSharePrice).toLocaleString('en-US')}`
              : '-'
          }
        />
        <SummaryItem
          title={<AppFormattedMessage id="PERCENT_CAPITAL_OWNED_POST_CONVERSION" />}
          value={`${toRound(capitalOwned * 100)}%`}
        />
        <SummaryItem
          title={<AppFormattedMessage id="SHARES_CONVERTED" />}
          value={`${toRound(convertedShares, 0).toLocaleString('en-US')}`}
        />
        {(docusignFileLinks?.length ?? 0) > 0 ? (
          <RecordAttachedFilesPreview
            docusignFileLinks={docusignFileLinks}
            files={filesLinks?.map((link) => ({
              docLink: link,
              doc: { name: getS3FileOriginalName(link) || link },
            }))}
            onDocumentClick={onDocumentClick}
          />
        ) : (
          <SummaryItem
            className="pb-0"
            title={<AppFormattedMessage id="ATTACHED_FILES_TITLE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              <RecordAttachedFilesPreview
                files={filesLinks?.map((link) => ({
                  docLink: link,
                  doc: { name: getS3FileOriginalName(link) || link },
                }))}
                onDocumentClick={onDocumentClick}
              />
            }
          />
        )}
      </div>
    </SafeDropDown>
  );
};
